import { useState, useEffect } from 'react';
import { Modal, Button, Alert, useToast } from '@/shared/components/ui';
import { tipoUsuarioService } from '../api/tipoUsuarioService';
import { TipoUsuarioDetalleDto } from '@/core/sdk/modules/administracion/tipoUsuarioClient';
import { MENSAJES_ERROR, TEXTOS_UI } from '../constants';
import styles from './TipoUsuarioModals.module.css';

interface Props {
    isOpen: boolean;
    tipoUsuarioId: number;
    onClose: () => void;
    onSuccess: () => void;
}

export const DeleteConfirmModal = ({ isOpen, tipoUsuarioId, onClose, onSuccess }: Props) => {
    const [tipoUsuario, setTipoUsuario] = useState<TipoUsuarioDetalleDto | null>(null);
    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(true);
    const { showToast } = useToast();

    useEffect(() => {
        loadTipoUsuario();
    }, [tipoUsuarioId]);

    const loadTipoUsuario = async () => {
        try {
            setLoadingData(true);
            const data = await tipoUsuarioService.getById(tipoUsuarioId);
            setTipoUsuario(data);
        } catch (error: any) {
            showToast({
                type: 'error',
                message: error.message || 'Error al cargar tipo de usuario'
            });
            onClose();
        } finally {
            setLoadingData(false);
        }
    };

    const handleDelete = async () => {
        try {
            setLoading(true);
            await tipoUsuarioService.delete(tipoUsuarioId);

            showToast({
                type: 'success',
                message: MENSAJES_ERROR.ELIMINADO_EXITOSO
            });

            onSuccess();
        } catch (error: any) {
            const errorMessage = error.response?.data?.message ||
                error.message ||
                MENSAJES_ERROR.ERROR_ELIMINAR;

            showToast({
                type: 'error',
                message: errorMessage
            });

            console.error('[DeleteTipoUsuario] Error:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Eliminar Tipo de Usuario"
            size="md"
        >
            {loadingData ? (
                <div className={styles.loadingContainer}>
                    <p>{TEXTOS_UI.CARGANDO_DATOS}</p>
                </div>
            ) : (
                <div className={styles.deleteContent}>
                    <p className={styles.deleteWarning}>
                        {TEXTOS_UI.CONFIRMAR_ELIMINACION_TITULO} <strong>{tipoUsuario?.nombre}</strong>?
                    </p>

                    {tipoUsuario && tipoUsuario.cantidadUsuarios > 0 && (
                        <Alert type="warning">
                            <p>
                                <strong>{TEXTOS_UI.ADVERTENCIA_PREFIJO}</strong> {TEXTOS_UI.ADVERTENCIA_USUARIOS} <strong>{tipoUsuario.cantidadUsuarios}</strong> {TEXTOS_UI.ADVERTENCIA_SUFIJO}
                            </p>
                            <p>
                                {TEXTOS_UI.NO_PUEDE_ELIMINAR}
                            </p>
                        </Alert>
                    )}

                    {tipoUsuario && tipoUsuario.cantidadUsuarios === 0 && (
                        <p className={styles.deleteNote}>
                            {TEXTOS_UI.NOTA_ELIMINACION}
                        </p>
                    )}

                    <div className={styles.actions}>
                        <Button
                            variant="secondary"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="danger"
                            onClick={handleDelete}
                            disabled={loading || (tipoUsuario?.cantidadUsuarios || 0) > 0}
                            loading={loading}
                        >
                            {loading ? TEXTOS_UI.BOTON_ELIMINANDO : TEXTOS_UI.BOTON_ELIMINAR}
                        </Button>
                    </div>
                </div>
            )}
        </Modal>
    );
};
